import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ModalController, NavController, Platform, IonicModule } from '@ionic/angular';
import { images } from '../../_shared/data/image';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { ToastService } from '../../_core/services/toast.service';
import { LoaderService } from '../../_core/services/loader.service';
import { finalize, take } from 'rxjs/operators';
import { CountriesPage } from '../../countries/countries.page';
import { SsoButtonsComponent } from '../../_shared/components/sso-buttons/sso-buttons.component';
import { CommonModule } from '@angular/common';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { IS_NEW_UI_FLOW } from '../../../environments/environment';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  standalone: true,
  imports: [IonicModule, SsoButtonsComponent, ReactiveFormsModule, CommonModule, TranslateModule],
  providers: []
})
export class SigninComponent {
  private platform = inject(Platform);
  @Output() goBackPage = new EventEmitter<string>();
  @Output() forgotPasswordPage = new EventEmitter<string>();
  @Input({ required: false }) isLoginModal = false;
  signinWithEmailForm: FormGroup;
  signinWithPhoneForm: FormGroup;
  isPhoneLogin: boolean = false;
  isEmailLogin: boolean = false;
  showPassword: boolean = false;
  public logoSquare = images.logoSquare;
  isBiometricLoginEnabled: boolean = false;
  isLoading: boolean = false;
  selectedCountryCode: string = '+1';
  public screenHeight: number = this.platform.height();

  private normalizeEmail(value: string | null | undefined): string {
    return value?.trim().toLowerCase() ?? '';
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private route: ActivatedRoute
  ) {
    this.signinWithEmailForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.signinWithPhoneForm = this.fb.group({
      country_code: ['+1', [Validators.required, Validators.pattern('([+]{1})([0-9]{1,3})')]],
      phone: ['', [Validators.required, Validators.pattern('([1-9]{1})[0-9]{5,15}')]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  async onSignIn() {
    let credentials;
    if (this.isEmailLogin) {
      if (this.signinWithEmailForm.valid) {
        const normalizedEmail = this.normalizeEmail(this.signinWithEmailForm.value.email);
        this.signinWithEmailForm.patchValue(
          { email: normalizedEmail },
          { emitEvent: false }
        );
        credentials = {
          emailOrPhone: normalizedEmail,
          password: this.signinWithEmailForm.value.password
        }
        this.isLoading = true;
        await this.loaderService.present();
      } else {
        Object.keys(this.signinWithEmailForm.controls).forEach(key => {
          const control = this.signinWithEmailForm.get(key);
          control?.markAsTouched();
        });
        await this.toastService.presentDangerToast('Please fill in all required fields correctly.');
        return;
      }
    } else {
      if (this.signinWithPhoneForm.valid) {
        this.isLoading = true;
        await this.loaderService.present();
        credentials = {
          emailOrPhone: this.signinWithPhoneForm.value.phone,
          country_code: this.signinWithPhoneForm.value.country_code,
          password: this.signinWithPhoneForm.value.password
        }
      } else {
        Object.keys(this.signinWithPhoneForm.controls).forEach(key => {
          const control = this.signinWithPhoneForm.get(key);
          control?.markAsTouched();
        });
        await this.toastService.presentDangerToast('Please fill in all required fields correctly.');
        return;
      }
    }

      this.authService.signIn(credentials)
        .pipe(
          take(1),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe({
          next: async (response) => {
            try {
              if (response?.data?.accessToken || response?.data?.token) {
                localStorage.removeItem('redirectAfterLogin');
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('accessToken', response.data.accessToken);

                if (response.data.refreshToken) {
                  localStorage.setItem('refreshToken', response.data.refreshToken);
                }

                if (response.data.cognitoUsername) {
                  localStorage.setItem('cognitoUsername', response.data.cognitoUsername);
                }

                await this.authService.getCognitoUser();
                // await this.toastService.presentToast('Login successful!', 'success');
                if (!this.isLoginModal) {
                  // Check for returnTo URL first (for /join/{team} redirects)
                  const returnTo = localStorage.getItem('returnTo');
                  if (returnTo) {
                    localStorage.removeItem('returnTo');
                    this.navCtrl.navigateRoot(returnTo);
                  } else {
                    this.navCtrl.navigateRoot('/main-menu');
                  }
                } else {
                  this.modalCtrl.dismiss();
                }
              } else {
                await this.toastService.presentDangerToast('Invalid response from server');
              }
            } finally {
              await this.loaderService.dismiss();
            }
          },
          error: async (error) => {
            try {
              if (error.status === 'EMAIL_REQUIRED') {
                this.navCtrl.navigateForward(`/${IS_NEW_UI_FLOW ? 'auth' : 'login'}/update-email`, {
                  state: {
                    phone: this.signinWithEmailForm.value.email,
                    password: this.signinWithEmailForm.value.password
                  }
                });

                await this.toastService.presentDangerToast(error.message);
                return;
              }
              const errorMessage = error?.error?.message || error?.message || 'Something went wrong';
              await this.toastService.presentDangerToast(errorMessage);
            } finally {
              // Dismiss loader even on error
              await this.loaderService.dismiss();
            }
          }
        });
    }

  get email() {
    return this.signinWithEmailForm.get('email');
  }

  get isEmailInvalid() {
    return this.email?.invalid && this.email?.touched;
  }

  continueWithEmail() {
    this.isEmailLogin = true;
  }

  continueWithPhone() {
    this.isPhoneLogin = true;
  }

  biometricLogin() {
    this.router.navigate([`/${IS_NEW_UI_FLOW ? 'auth' : 'login'}/biometric-login`]);
  }
  async forgotPassword() {
    if (this.isLoginModal) {
      this.forgotPasswordPage.emit('forgot-password');
    } else {
      this.router.navigate(['/auth/forgot-password'])
    }
  }

  goBack() {
    this.goBackPage.emit('login');
  }

  async loginWithSSO(provider: 'Google' | 'Apple') {
    try {
      const ssoUrl = await this.authService.getSSOLoginUrlWithAppDetection(provider);

      // Store current URL for return after SSO
      this.authService.setSsoReturnUrl('/main-menu');

      // Use window.location.href to force external browser
      // This works for both iOS and Android
      window.location.href = ssoUrl;

    } catch (error) {
      await this.toastService.presentDangerToast('Failed to open SSO login. Please try again.');
    } finally {
      // Dismiss loading indicator
      await this.loaderService.dismiss();
    }
  }



  async getCountryCode() {
    const modal = await this.modalCtrl.create({
      component: CountriesPage,
      componentProps: {
        showCountryName: true,
      }
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.country) {
      this.selectedCountryCode = data.country;
      const countryCodeControl = this.signinWithPhoneForm.get('country_code');
      if (countryCodeControl) {
        countryCodeControl.setValue(data.country);
      }
    }
  }
}